import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { formatDate } from '../utils/formatDate';
import { getImageUrl, getImageAlt, getPlaceholderImage } from '../utils/imageHelpers';
import { useLocalizedPath } from '../hooks/useLocalizedPath';

/**
 * ArticleCard component for displaying article previews
 * Supports three variants: featured, standard, and compact
 * Requirements: 2.3, 6.3
 * 
 * @param {Object} props - Component props
 * @param {Object} props.article - Article data object
 * @param {string} props.variant - Card variant: 'featured', 'standard', or 'compact'
 */
const ArticleCard = ({ article, variant = 'standard' }) => {
  const localizedPath = useLocalizedPath();
  const { lang } = useParams();
  
  if (!article) {
    console.log('ArticleCard: No article provided');
    return null;
  }

  // Handle Strapi response format - data is in attributes
  const articleData = article.attributes || article;

  console.log('ArticleCard rendering:', { 
    variant, 
    title: articleData.title,
    slug: articleData.slug
  });

  const {
    slug,
    title,
    excerpt,
    author,
    publishedAt,
    featuredImage,
    categories = [],
    _type,
  } = articleData;

  // Get image URL or placeholder
  const imageUrl = getImageUrl(featuredImage) || getPlaceholderImage(800, 600);
  const imageAlt = getImageAlt(featuredImage, title);

  // Format publication date
  const formattedDate = formatDate(publishedAt, lang || 'en');

  // Determine the correct link path based on content type
  const getLinkPath = () => {
    if (_type === 'conference') return `/conferences/${slug}`;
    if (_type === 'activity') return `/activities/${slug}`;
    return `/news/${slug}`;
  };
  const linkPath = getLinkPath();

  // Render category tags
  const renderCategories = () => {
    if (!categories || categories.length === 0) return null;

    // Handle both Strapi response formats
    const categoryList = categories.data || categories;

    return (
      <div className="flex flex-wrap gap-2 mb-2">
        {categoryList.map((category) => {
          const categoryData = category.attributes || category;
          return (
            <Link
              key={categoryData.slug || categoryData.name}
              to={localizedPath(`/news?category=${categoryData.slug}`)}
              onClick={(e) => e.stopPropagation()}
              className="inline-block px-2 py-1 text-xs font-semibold text-primary-700 bg-primary-100 rounded hover:bg-primary-200 transition-colors"
            >
              {categoryData.name}
            </Link>
          );
        })}
      </div>
    );
  };

  // Featured variant - large card with prominent image
  if (variant === 'featured') {
    return (
      <Link
        to={localizedPath(linkPath)}
        className="block group overflow-hidden rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300 bg-white"
      >
        {/* Large featured image */}
        <div className="relative h-96 overflow-hidden">
          <img
            src={imageUrl}
            alt={imageAlt}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
          
          {/* Content overlay on image */}
          <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
            {renderCategories()}
            <h2 className="text-3xl font-bold mb-3 group-hover:text-primary-200 transition-colors">
              {title}
            </h2>
            {excerpt && (
              <p className="text-lg text-gray-100 mb-3 line-clamp-2">
                {excerpt}
              </p>
            )}
            <div className="flex items-center text-sm text-gray-200">
              {author && (
                <>
                  <span className="font-medium">{author}</span>
                  <span className="mx-2">•</span>
                </>
              )}
              <time dateTime={publishedAt}>{formattedDate}</time>
            </div>
          </div>
        </div>
      </Link>
    );
  }

  // Compact variant - small card with minimal metadata
  if (variant === 'compact') {
    return (
      <Link
        to={localizedPath(linkPath)}
        className="flex group bg-white rounded-lg overflow-hidden hover:shadow-md transition-shadow duration-300"
      >
        {/* Small thumbnail */}
        <div className="flex-shrink-0 w-24 h-24 overflow-hidden">
          <img
            src={imageUrl}
            alt={imageAlt}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        </div>

        {/* Content */}
        <div className="flex-1 p-3 min-w-0">
          {renderCategories()}
          <h3 className="text-sm font-semibold text-gray-900 mb-1 line-clamp-2 group-hover:text-primary-600 transition-colors">
            {title}
          </h3>
          <div className="flex items-center text-xs text-gray-500">
            <time dateTime={publishedAt}>{formattedDate}</time>
          </div>
        </div>
      </Link>
    );
  }

  // Standard variant - medium card with image, title, excerpt, metadata
  return (
    <Link
      to={localizedPath(linkPath)}
      className="block group bg-white rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-shadow duration-300"
    >
      {/* Image */}
      <div className="relative h-48 overflow-hidden">
        <img
          src={imageUrl}
          alt={imageAlt}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
        />
      </div>

      {/* Content */}
      <div className="p-4">
        {renderCategories()}
        
        <h3 className="text-xl font-bold text-gray-900 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
          {title}
        </h3>

        {excerpt && (
          <p className="text-gray-600 mb-3 line-clamp-3">
            {excerpt}
          </p>
        )}

        {/* Metadata */}
        <div className="flex items-center text-sm text-gray-500 pt-3 border-t border-gray-100">
          {author && (
            <>
              <span className="font-medium text-gray-700">{author}</span>
              <span className="mx-2">•</span>
            </>
          )}
          <time dateTime={publishedAt}>{formattedDate}</time>
        </div>
      </div>
    </Link>
  );
};

export default ArticleCard;
